import { useState } from 'react';
import { DollarSign, Receipt, Upload } from 'lucide-react';
import { PAYMENT_TRACKING, PAYMENT_STATUS } from './mockVendorData';

function PaymentStatusBadge({ status }) {
  const config = PAYMENT_STATUS[status];
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ring-1 ${config.className}`}
    >
      {config.label}
    </span>
  );
}

export default function FinancialsPanel({ payments = PAYMENT_TRACKING }) {
  const [uploadMsg, setUploadMsg] = useState('');
  const [showUpload, setShowUpload] = useState(false);

  const totalPending = payments
    .filter((p) => p.status !== 'paid')
    .reduce((s, p) => s + p.amountAed, 0);
  const totalPaid = payments
    .filter((p) => p.status === 'paid')
    .reduce((s, p) => s + p.amountAed, 0);

  const handleUpload = () => {
    setUploadMsg('Invoice INV-VND-2026-0612 uploaded successfully — OGTS finance notified.');
    setShowUpload(false);
    setTimeout(() => setUploadMsg(''), 4000);
  };

  return (
    <section className="overflow-hidden rounded-xl border border-logi-grid bg-white backdrop-blur-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-logi-grid px-4 py-3">
        <div className="flex items-center gap-2">
          <Receipt className="h-4 w-4 text-emerald-700" strokeWidth={1.75} />
          <div>
            <h3 className="text-sm font-bold text-logi-text">Financials &amp; Invoice Upload</h3>
            <p className="text-[11px] text-logi-text0">Payment tracking · Vendor billing</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowUpload(true)}
          className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-indigo-600 to-indigo-500 px-4 py-2 text-xs font-bold text-white shadow-lg shadow-indigo-500/20 transition hover:from-indigo-500 hover:to-indigo-400"
        >
          <Upload className="h-4 w-4" />
          Upload Invoice
        </button>
      </div>

      {uploadMsg && (
        <div className="border-b border-emerald-500/20 bg-emerald-50 px-4 py-2 text-xs font-medium text-emerald-700">
          {uploadMsg}
        </div>
      )}

      <div className="grid grid-cols-2 gap-px border-b border-logi-grid/80 bg-logi-silver/80">
        <div className="bg-white px-4 py-3">
          <p className="text-[10px] font-semibold uppercase text-logi-text0">Pending Payout</p>
          <p className="font-mono text-lg font-bold text-amber-700">
            {totalPending.toLocaleString()} <span className="text-xs">AED</span>
          </p>
        </div>
        <div className="bg-white px-4 py-3">
          <p className="text-[10px] font-semibold uppercase text-logi-text0">Paid (MTD)</p>
          <p className="font-mono text-lg font-bold text-emerald-700">
            {totalPaid.toLocaleString()} <span className="text-xs">AED</span>
          </p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead>
            <tr className="border-b border-logi-grid/80 text-[10px] uppercase tracking-wider text-logi-text0">
              <th className="px-4 py-2 font-semibold">Payment Ref</th>
              <th className="px-4 py-2 font-semibold">Job</th>
              <th className="px-4 py-2 font-semibold">Description</th>
              <th className="px-4 py-2 font-semibold">Amount</th>
              <th className="px-4 py-2 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-logi-grid/30">
            {payments.map((row) => (
              <tr key={row.id} className="hover:bg-logi-silver">
                <td className="px-4 py-2 font-mono text-xs text-indigo-400">{row.id}</td>
                <td className="px-4 py-2 font-mono text-xs text-logi-muted">{row.jobId}</td>
                <td className="px-4 py-2 text-xs text-logi-muted">{row.description}</td>
                <td className="px-4 py-2 font-mono text-xs font-semibold text-logi-text">
                  <span className="inline-flex items-center gap-0.5">
                    <DollarSign className="h-3 w-3 text-logi-text0" />
                    {row.amountAed.toLocaleString()} AED
                  </span>
                </td>
                <td className="px-4 py-2">
                  <PaymentStatusBadge status={row.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showUpload && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-logi-silver/80 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-logi-grid bg-white p-6 shadow-2xl">
            <h4 className="text-lg font-bold text-logi-text">Upload Vendor Invoice</h4>
            <p className="mt-1 text-sm text-logi-muted">Attach PDF for completed or in-progress jobs</p>
            <div className="mt-4 space-y-3">
              <select className="w-full rounded-xl border border-logi-grid bg-white px-3 py-2.5 text-sm text-logi-text outline-none focus:border-indigo-500/50">
                <option>JOB-8843 — LPG Sharjah (4,800 AED)</option>
                <option>JOB-8842 — Gas Oil Jebel Ali (7,200 AED)</option>
              </select>
              <div className="rounded-xl border border-dashed border-logi-grid bg-logi-silver/80 px-4 py-8 text-center text-sm text-logi-text0">
                Drop invoice PDF or click to browse
              </div>
            </div>
            <div className="mt-5 flex gap-2">
              <button
                type="button"
                onClick={() => setShowUpload(false)}
                className="flex-1 rounded-xl py-2.5 text-sm text-logi-muted hover:text-logi-text"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleUpload}
                className="flex-1 rounded-xl bg-indigo-600 py-2.5 text-sm font-bold text-white hover:bg-indigo-500"
              >
                Submit Invoice
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
